// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { StatsTableAnchorCell as StatsTableAnchorCell } from '../../../../interfaces/hce/hce-stats';

export type CellItem = string | number | StatsTableAnchorCell | undefined;
export type TableRecords = Record<string, CellItem>[];

@Component({
  selector: 'app-hce-stats-table',
  standalone: true,
  templateUrl: './hce-stats-table.component.html',
  styleUrls: ['./hce-stats-table.component.css'],
  imports: [
    CommonModule,
    MatTableModule,
  ],
})
export class HceStatsTableComponent implements OnChanges {

  @Input() data?: TableRecords = [];
  @Input() columnKeys: string[] = [];
  @Input() columnTitles: string[] = [];
  @Output() cellFilterRequest = new EventEmitter<{
    chr?: string | number;
    svType?: string;
  }>();

  constructor(
  ) {}

  dataSource = new MatTableDataSource<Record<string, CellItem>>([]);
  displayedColumns: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Reuse the column key array directly as mat-table's displayedColumns
    this.displayedColumns = [...this.columnKeys];

    // update data source
    this.dataSource.data = Array.isArray(this.data) ? this.data : [];
  }

  /**
   * Get the column title at the specified index
   */
  getColumnTitle(index: number, key: string): string {
    return this?.columnTitles?.[index] ?? key;
  }

  /* 
   * Whether the cell should be displayed as an anchor
   */
  hasAnchor(cell: CellItem | null | undefined): boolean {
    if (cell && typeof cell === 'object') {
      // Treat as an anchor if chr and svType are not undefined/null
      return (
        ((cell as StatsTableAnchorCell).chr !== undefined && (cell as StatsTableAnchorCell).chr !== null) ||
        ((cell as StatsTableAnchorCell).svType !== undefined && (cell as StatsTableAnchorCell).svType !== null)
      );
    }
    return false;
  }

  getDisplayValue(cell: CellItem | null | undefined): string | number {
    if (cell && typeof cell === 'object') {
      return (cell as StatsTableAnchorCell).value ?? '';
    }
    return (cell ?? '') as string;
  }
  
  getCell(row: Record<string, CellItem> | null | undefined, col: string): StatsTableAnchorCell | null {
    if (!row) {
      return null
    }

    // Treat as StatsTableAnchorCell when the value is an object
    const raw = (row as any)[col];
    if (raw && typeof raw === 'object' && 'value' in raw) {
      return raw as StatsTableAnchorCell;
    }

    // If it's a string, use it as the value
    if (typeof raw === 'string') {
      return { value: raw }
    };

    return null;
  }

  getDisplayValueFromRow(row: Record<string, CellItem> | null | undefined, col: string): string | number {
    return this.getCell(row, col)?.value ?? '';
  }

  onCellAnchorClick(row: Record<string, CellItem> | null | undefined, col: string): void {
    const cell = this.getCell(row, col);
    this.cellFilterRequest.emit({
      chr: cell?.chr,
      svType: cell?.svType,
    });
  }
}
